import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/PrefsContext";
import { LOGO_URL } from "@/components/Logo";
import { Eye, EyeOff, Lock, User, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username || !password) {
      setError(t("Please enter your username and password."));
      return;
    }
    setLoading(true);
    const res = await login(username.trim(), password);
    setLoading(false);
    if (res.ok) {
      navigate("/");
    } else {
      setError(res.error || t("Invalid username or password."));
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative items-center justify-center overflow-hidden"
        style={{ background: "linear-gradient(150deg, #2A0A4A 0%, #5A189A 55%, #7B2CBF 100%)" }}>
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #D4AF37 0, transparent 35%), radial-gradient(circle at 80% 70%, #D4AF37 0, transparent 30%)" }} />
        <div className="relative z-10 text-center px-12">
          <img src={LOGO_URL} alt="Magic Touch" className="w-44 h-44 mx-auto rounded-full object-cover ring-4 ring-[#D4AF37]/60 shadow-2xl" />
          <h1 className="font-display text-4xl font-bold text-white mt-8">Magic Touch</h1>
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm font-semibold mt-2">Professional Cleaning</p>
          <p className="text-white/70 mt-6 max-w-sm mx-auto leading-relaxed">
            {t("The complete management suite for premium residential & commercial cleaning.")}
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#F6F4FA]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img src={LOGO_URL} alt="Magic Touch" className="w-28 h-28 rounded-full object-cover ring-4 ring-[#D4AF37]/60 shadow-xl" />
          </div>

          <div className="mt-card p-8">
            <h2 className="font-display text-3xl font-bold text-[#121212] text-center">{t("Welcome Back")}</h2>
            <p className="text-[#52525B] text-center text-sm mt-1.5">{t("Sign in to your Magic Touch dashboard")}</p>

            {error && (
              <div className="mt-5 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="login-error">
                <AlertCircle size={18} /> {error}
              </div>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4" autoComplete="off">
              <div>
                <label className="text-sm font-medium text-[#52525B]">{t("Username")}</label>
                <div className="relative mt-1.5">
                  <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9b8bb5]" />
                  <input
                    type="text"
                    name="mt_username"
                    autoComplete="off"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t("Enter your username")}
                    data-testid="login-username"
                    style={{ color: "#121212", backgroundColor: "#ffffff" }}
                    className="login-input w-full border border-[#e7ddf5] rounded-xl pl-11 pr-4 py-3 text-sm text-[#121212] placeholder:text-[#9b8bb5] outline-none focus:ring-2 focus:ring-[#5A189A]/20 focus:border-[#5A189A] transition"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#52525B]">{t("Password")}</label>
                <div className="relative mt-1.5">
                  <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9b8bb5]" />
                  <input
                    type={show ? "text" : "password"}
                    name="mt_password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("Enter your password")}
                    data-testid="login-password"
                    style={{ color: "#121212", backgroundColor: "#ffffff" }}
                    className="login-input w-full border border-[#e7ddf5] rounded-xl pl-11 pr-11 py-3 text-sm text-[#121212] placeholder:text-[#9b8bb5] outline-none focus:ring-2 focus:ring-[#5A189A]/20 focus:border-[#5A189A] transition"
                  />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#9b8bb5]">
                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="btn-gold-hover w-full bg-[#5A189A] text-white font-semibold rounded-xl py-3 text-sm shadow-md hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading ? t("Signing in...") : t("Sign In")}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-[#9b8bb5] mt-6">
            © {new Date().getFullYear()} Magic Touch Professional Cleaning
          </p>
        </div>
      </div>
    </div>
  );
}
